import React, {useState, useRef} from 'react'
import {useCreateWalletCardPublicMutation} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import './FormWalletCardPublic.scss'
import {useDispatch} from 'react-redux'
import {startLoadding, stopLoading} from '../../../../../app/Redux/FrontEnd/Loading/LoadingSlice'
import * as Yup from 'yup'
import {DataShareContact, FormWalletCardPublicProps, processFormSettings} from '../type'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {ContactData, PromoAction} from '../../../../../services/cellcast.v2.types'
import {
  DEFAULT_LENGTH,
  MAX_LENGTH_ADDRESS,
  MAX_LENGTH_COMPANY_NAME,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_FULL_NAME,
  MAX_LENGTH_JOB_TITLE,
  MAX_LENGTH_NOTES,
  MAX_LENGTH_WEBSITE,
  MIN_LENGTH_FULL_NAME,
  DEFAULT_COUNTRY_CODE,
  COUNTRY_CODES,
} from '../../../../Utils/Constant/WalletCard'
import PhoneInput from '../../../../Components/PhoneInput'
import '../../../../Components/Style/PhoneInput.scss'
import {validatePhoneNumberRegionWithMessage} from '../../../../Utils/_helpers'
import InputVehicleTypeOption from '../../../../Components/InputVehicleTypeOption'

const FormWalletCardPublic = ({
  name,
  userID,
  source,
  targetID,
  setShowPopup,
  picture,
  setDataShareContact,
  promoSettings,
  formSettings = [],
  formBackgroundColor,
  formLabelColor,
  vehicleTypeOptions,
}: FormWalletCardPublicProps) => {
  const [createWalletCardPublic, {isLoading}] = useCreateWalletCardPublicMutation()
  const dispatch = useDispatch()
  const {t} = useI18n()


  // Process and sort form settings by order
  const sortedFormSettings = processFormSettings(formSettings)

  // Create refs for form fields dynamically
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})
  const submittingRef = useRef(false)

  // Initialize form data based on formSettings
  const initializeFormData = () => {
    const initialData: Record<string, string | string[]> = {
      source: '',
      promoSettings: [],
      countryCode: DEFAULT_COUNTRY_CODE, // Default country code for phone
      interestedVehicleTypes: [],
    }

    sortedFormSettings.forEach((setting) => {
      initialData[setting.key] = ''
    })

    return initialData
  }

  const [formData, setFormData] = useState(initializeFormData())

  // Initialize errors based on formSettings
  const initializeErrors = () => {
    const initialErrors: Record<string, string> = {
      promoAction: '',
    }

    sortedFormSettings.forEach((setting) => {
      initialErrors[setting.key] = ''
    })

    return initialErrors
  }

  const [errors, setErrors] = useState(initializeErrors())

  const [selectedPromos, setSelectedPromos] = useState<PromoAction[]>([])

  // Schema for validating email using Yup
  const emailSchema = Yup.string()
    .email(t('WALLET_CARD.VALIDATION.INVALID_EMAIL'))
    .required(t('WALLET_CARD.VALIDATION.REQUIRED', {name: 'Email'}))

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
    // Reset error message when user modifies the input
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({...prev, [name]: ''}))
    }
  }

  // Handle phone input change from PhoneInput component
  const handlePhoneChange = (phone: string, countryCode: string) => {
    setFormData((prev) => ({
      ...prev,
      phone: phone,
      countryCode: countryCode, // This will be the actual country code like +61, +1, etc.
    }))
    // Reset phone error when user modifies the input
    if (errors.phone) {
      setErrors((prev) => ({...prev, phone: ''}))
    }
  }

  const isValidEmail = (email: string) => {
    if (!email) return true
    try {
      emailSchema.validateSync(email)
      return true
    } catch {
      return false
    }
  }

  // Enhanced validation for different field types
  const validateField = (key: string, value: string) => {
    const setting = sortedFormSettings.find((s) => s.key === key)
    if (!setting) return ''

    // Required field validation
    if (setting.isRequired && !value.trim()) {
      return t('WALLET_CARD.VALIDATION.REQUIRED', {name: setting.label})
    }

    if (!value.trim()) return '' // Skip validation for empty optional fields

    // Specific field validations
    switch (key) {
      case 'fullName':
        if (value.trim().length < MIN_LENGTH_FULL_NAME) {
          return t('WALLET_CARD.VALIDATION.MIN_LENGTH', {
            name: setting.label,
            minLength: MIN_LENGTH_FULL_NAME,
          })
        }
        if (value.trim().length > MAX_LENGTH_FULL_NAME) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {name: setting.label, maxLength: 50})
        }
        break

      case 'phone':
        // Validate phone number format - only allow digits, spaces, parentheses, and plus sign
        const phoneFormatRegex = /^[\d\s()+]+$/
        if (
          !phoneFormatRegex.test(value) ||
          value.includes('.') ||
          value.includes('-') ||
          value.includes(',')
        ) {
          return t('WALLET_CARD.VALIDATION.INVALID_PHONE_NUMBER_FORMAT')
        }

        const countryCode = formData.countryCode as string
        // Convert countryCode (+61) back to region code (AU) for validation
        const regionCode =
          COUNTRY_CODES.find((c: any) => c.countryCode === countryCode)?.code || 'AU'
        try {
          const result = validatePhoneNumberRegionWithMessage({
            number: value,
            region: regionCode,
            validateDuplicate: false,
            contacts: new Map(),
          })
          if (!result.isValid) {
            return result.errorMessage
          }
        } catch (err) {
          return t('WALLET_CARD.VALIDATION.INVALID_PHONE')
        }
        break

      case 'email':
        if (!isValidEmail(value)) {
          return t('WALLET_CARD.VALIDATION.INVALID_EMAIL')
        }
        break

      case 'website':
        if (value.trim().length > MAX_LENGTH_WEBSITE) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
            name: setting.label,
            maxLength: MAX_LENGTH_WEBSITE,
          })
        }
        const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/
        if (!urlRegex.test(value.trim())) {
          return t('WALLET_CARD.VALIDATION.INVALID_URL')
        }
        break

      case 'companyName':
        if (value.trim().length > MAX_LENGTH_COMPANY_NAME) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
            name: setting.label,
            maxLength: MAX_LENGTH_COMPANY_NAME,
          })
        }
        break
      case 'jobTitle':
        if (value.trim().length > MAX_LENGTH_JOB_TITLE) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
            name: setting.label,
            maxLength: MAX_LENGTH_JOB_TITLE,
          })
        }
        break

      case 'address':
        if (value.trim().length > MAX_LENGTH_ADDRESS) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
            name: setting.label,
            maxLength: MAX_LENGTH_ADDRESS,
          })
        }
        break

      case 'notes':
        if (value.trim().length > MAX_LENGTH_NOTES) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
            name: setting.label,
            maxLength: MAX_LENGTH_NOTES,
          })
        }
        break

      case 'interestedVehicleType':
        // No extra rules besides required/non-empty (handled above)
        break

      default:
        // Generic validation for custom fields
        if (value.trim().length > DEFAULT_LENGTH) {
          return t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
            name: setting.label,
            maxLength: DEFAULT_LENGTH,
          })
        }
        break
    }

    return ''
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const {name, value} = e.target
    setErrors((prev) => ({...prev, [name]: validateField(name, value)}))
  }

  const validateForm = (): {isValid: boolean; nextErrors: Record<string, string>} => {
    let isValid = true
    const newErrors = {...errors}

    // Validate form fields based on formSettings
    sortedFormSettings.forEach((setting) => {
      if (setting.key === 'interestedVehicleType') {
        const selected = (formData.interestedVehicleTypes as string[]) || []
        const error =
          setting.isRequired && selected.length === 0
            ? t('WALLET_CARD.VALIDATION.REQUIRED', {name: setting.label})
            : ''
        newErrors[setting.key] = error
        if (error) isValid = false
        return
      }

      const fieldValue = formData[setting.key]
      const stringValue = typeof fieldValue === 'string' ? fieldValue : ''
      const error = validateField(setting.key, stringValue)
      newErrors[setting.key] = error
      if (error) isValid = false
    })

    setErrors(newErrors)
    return {isValid, nextErrors: newErrors}
  }

  const handlePromoChange = (promo: PromoAction) => {
    setSelectedPromos((prev) => {
      const isSelected = prev.some((p) => p.id === promo.id)
      if (isSelected) {
        return prev.filter((p) => p.id !== promo.id)
      } else {
        return [...prev, promo]
      }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading || submittingRef.current) return
    const {isValid, nextErrors} = validateForm()
    if (isValid) {
      try {
        submittingRef.current = true
        dispatch(startLoadding())
        // Wait for 300ms before submitting
        await new Promise((resolve) => setTimeout(resolve, 300))

        const selectedPromoIds = selectedPromos.map((promo) => promo.id)

        // Prepare the API request payload
        const filteredFormData = Object.fromEntries(
          Object.entries(formData).filter(
            ([_, value]) => value !== '' && value !== null && value !== undefined
          )
        )
        const trimmedFormData = Object.entries(filteredFormData).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: typeof value === 'string' ? value.trim() : value,
          }),
          {}
        )

        const payload = {
          ...trimmedFormData,
          user: userID,
          source: source,
          targetId: targetID,
          promoSettings: selectedPromoIds,
        } as ContactData

        // Call the API mutation
        const response = await createWalletCardPublic(payload).unwrap()

        // Display the success page instead of a toast notification
        if (response?.data?.id) {
          setShowPopup(false)
          setDataShareContact(response?.data as DataShareContact)
        }
      } catch (error: any) {
        // Show error notification
        toast.error(error.data?.message || 'Failed to submit your contact information')
      } finally {
        submittingRef.current = false
        dispatch(stopLoading())
      }
    } else {
      // Scroll to the first input with an error
      const firstErrorField = sortedFormSettings.find((setting) => Boolean(nextErrors[setting.key]))
      if (firstErrorField && fieldRefs.current[firstErrorField.key]) {
        fieldRefs.current[firstErrorField.key]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        fieldRefs.current[firstErrorField.key]?.focus()
      }
    }
  }

  // Helper function to get max length for input field
  const getMaxLength = (key: string) => {
    switch (key) {
      case 'fullName':
        return MAX_LENGTH_FULL_NAME
      case 'companyName':
        return MAX_LENGTH_COMPANY_NAME
      case 'jobTitle':
        return MAX_LENGTH_JOB_TITLE
      case 'email':
        return MAX_LENGTH_EMAIL
      case 'website':
        return MAX_LENGTH_WEBSITE
      case 'address':
        return MAX_LENGTH_ADDRESS
      case 'notes':
        return MAX_LENGTH_NOTES
      default:
        return DEFAULT_LENGTH
    }
  }

  // Helper function to determine if field should be textarea
  const shouldUseTextarea = (key: string) => {
    return key === 'notes'
  }

  // Helper function to determine if field is phone
  const isPhoneField = (key: string) => {
    return key === 'phone'
  }

  // Helper function to get input component
  const renderInputField = (setting: any) => {
    // Special handling for phone field
    if (isPhoneField(setting.key)) {
      return (
        <div>
          {/* Custom label for phone field to match form styling */}
          <label style={{color: formLabelColor}} className='mb-2'>
            {setting.label}
            {setting.isRequired && <span className='required-field'>*</span>}
          </label>
          <PhoneInput
            value={formData[setting.key] as string}
            countryCode={formData.countryCode as string}
            onChange={handlePhoneChange}
            onBlur={() => {
              const phoneValue = (formData[setting.key] as string) || ''
              setErrors((prev) => ({
                ...prev,
                [setting.key]: validateField(setting.key, phoneValue),
              }))
            }}
            placeholder={t('WALLET_CARD.PLACEHOLDER.PHONE')}
            required={setting.isRequired}
            error={errors[setting.key]}
            touched={!!errors[setting.key]}
            disabled={isLoading}
            name={setting.key}
            className='wallet-card-phone-input'
            label='' // Don't show PhoneInput's internal label
            color={formLabelColor}
          />
        </div>
      )
    }

    const inputProps = {
      ref: (ref: HTMLInputElement | HTMLTextAreaElement | null) => {
        fieldRefs.current[setting.key] = ref
      },
      name: setting.key,
      className: `form-control ${errors[setting.key] ? 'is-invalid' : ''}`,
      value: formData[setting.key] || '',
      onChange: handleChange,
      onBlur: handleBlur,
      disabled: isLoading,
      required: setting.isRequired,
      maxLength: getMaxLength(setting.key),
    }

    if (shouldUseTextarea(setting.key)) {
      return <textarea {...inputProps} rows={3} className='w-100 form-control' />
    }

    return <input {...inputProps} type={setting.key === 'email' ? 'email' : 'text'} />
  }

  return (
    <div
      className='form-wallet-card-public-container relative'
      style={{backgroundColor: formBackgroundColor}}
    >
      <div className='form-wallet-card-public-header'>
        <div className={`icon-container ${picture ? '' : 'd-none'}`}>
          <div className={`icon-circle ${picture ? 'logo-circle' : 'd-none'}`}>
            <img alt='Logo' src={picture} className='rounded-circle object-fit-cover w-100' />
          </div>
        </div>
        <div className='title-form-wallet-card-public'>
          <h3 style={{color: formLabelColor}}>
            {t('WALLET_CARD_PUBLIC.SHARE_CONTACT_INFORMATION', {name})}
          </h3>
        </div>
      </div>
      <form className='contact-form' onSubmit={handleSubmit}>
        {/* Promo Action */}
        {promoSettings?.length > 0 && (
          <div className='form-group mb-3'>
            <label style={{color: formLabelColor}}>
              {t('WALLET_CARD_PUBLIC.PROMO_ACTION')} <span className='required-field'>*</span>
            </label>
            <div className='d-flex gap-1 flex-column'>
              {promoSettings?.map((promo: PromoAction) => (
                <label
                  key={promo.id}
                  className={`btn d-flex align-items-center promo-action-button w-100 ${
                    selectedPromos.some((p) => p.id === promo.id) ? 'active-promo-action' : ''
                  }`}
                  onClick={() => handlePromoChange(promo)}
                >
                  <input
                    type='checkbox'
                    name='promoAction'
                    checked={selectedPromos.some((p) => p.id === promo.id)}
                    onChange={() => handlePromoChange(promo)}
                    className='absolute top-0 right-0 promo-action-checkbox'
                  />
                  <span className='promo-text'>{promo.name}</span>
                </label>
              ))}
            </div>
            {errors.promoAction && <div className='error-message'>{errors.promoAction}</div>}
          </div>
        )}

        {(() => {
          if (vehicleTypeOptions?.length === 0) {
            return null;
          }
          return (
            <div
              className='form-group mb-3'
              ref={(ref) => {
                fieldRefs.current.interestedVehicleType = ref
              }}
            >
                <label style={{color: formLabelColor}}>
                  {t('WALLET_CARD_PUBLIC.INTERESTED_VEHICLE_TYPE')}
                </label>

              <InputVehicleTypeOption
                value={(formData.interestedVehicleTypes as string[]) || []}
                onChange={(value) => {
                  setFormData((prev) => ({...prev, interestedVehicleTypes: value}))
                  setErrors((prev) => ({
                    ...prev
                  }))
                }}
                name='interestedVehicleType'
                userIdForPublicList={userID}
                allowCreate={false}
                allowedIds={vehicleTypeOptions}
                error={errors.interestedVehicleType}
              />
            </div>
          )
        })()}

        {/* Dynamic form fields based on formSettings with enhanced rendering */}
        {sortedFormSettings
          .filter((setting) => setting.key !== 'interestedVehicleType')
          .map(
          (setting) =>
            setting.isDisplay && (
              <div key={setting._id} className='form-group mb-3'>
                {/* For phone field, label and error are handled within renderInputField */}
                {!isPhoneField(setting.key) && (
                  <label style={{color: formLabelColor}}>
                    {setting.label}
                    {setting.isRequired && <span className='required-field'>*</span>}
                  </label>
                )}
                {renderInputField(setting)}
                {/* For phone field, error is handled by PhoneInput component */}
                {!isPhoneField(setting.key) && errors[setting.key] && (
                  <div className='error-message'>{errors[setting.key]}</div>
                )}
              </div>
            )
        )}
      </form>

      <div className='mx-auto d-flex flex-column align-items-center gap-2 mt-10 pb-2'>
        <button
          type='submit'
          className='submit-btn mb-2 fs-1 fw-bold'
          onClick={handleSubmit}
          disabled={isLoading || submittingRef.current}
        >
          {isLoading ? t('WALLET_CARD_PUBLIC.CONNECTING') : t('WALLET_CARD_PUBLIC.CONNECT')}
        </button>
        <span className='fs-5' style={{color: formLabelColor}}>
          {t('WALLET_CARD_PUBLIC.POWERED_BY')}
        </span>
      </div>
    </div>
  )
}

export default FormWalletCardPublic
